"use client";
import { contentfulLoader } from "@delicious-simplicity/next-image-contentful-loader";
import type {
    HotspotMarkerModel,
    HotspotModel,
} from "@lib/contentful/mappers/modules/hotspot/types";
import type { ImageModel } from "@lib/contentful/mappers/modules/image/types";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { aprimoLoader } from "@ecco/ui/aprimo-loader";
import {
    contentfulLoaderDefaultProps,
    isAprimoImage,
    isFlashCDNImage,
    protocolRelativeToAbsoluteUrl,
} from "@ecco/ui/next-image";
import type { FC } from "react";
import ImageMarker from "react-image-marker";
import type { CustomMarkerProps } from "./CustomMarker";
import { CustomMarker } from "./CustomMarker";
import { CustomMarkerProduct } from "./CustomMarkerProduct";
import { deviceSizeMap } from "@ecco/ui/tokens/device-sizes";

const CreateImageMarker = (
    image: ImageModel | undefined,
    markers: HotspotMarkerModel[],
    desktop?: boolean
) => {
    if (!image) return null;

    const src = protocolRelativeToAbsoluteUrl(image.url);
    const width = desktop ? deviceSizeMap[1920] : deviceSizeMap[768];

    return (
        <ImageMarker
            src={
                isAprimoImage(src) || isFlashCDNImage(src)
                    ? aprimoLoader({ src, width, quality: 80 })
                    : contentfulLoader({ src, width, quality: 80 }, contentfulLoaderDefaultProps)
            }
            alt={image.alt}
            markers={markers}
            markerComponent={(props: CustomMarkerProps) =>
                props.product
                    ? CustomMarkerProduct({ ...props, product: props.product })
                    : CustomMarker(props)
            }
            bufferLeft={16}
            bufferTop={16}
        />
    );
};

export const Hotspot: FC<HotspotModel> = ({
    title,
    description,
    gradient,
    image,
    mobileImage,
    markers,
    mobileMarkers,
}) => {
    return (
        <Box mt={6} position="relative">
            <Flex
                width={["100%", "100%", "100%"]}
                height={["500px", "100%", "100%"]}
                position="relative"
                flexDirection="column"
                justifyContent="flex-end"
                /* If there is a product image, the aspect ratio will be 1/1.
                We need this fixed values to give the container a specific height
                and mantain the image in the center with the reference to the x and y axis for the hotspot */
                display="flex"
                alignItems={["center", "flex-start"]}
                sx={{
                    "& .image-marker": {
                        width: "100%",
                        height: "100%",
                    },
                    "& img": {
                        height: "100%",
                    },
                }}
            >
                {/* We cannot handle responsiveness with the current library, so we will just hide/show both components
                    Is not possible use values from the hooks like md, lg from useThemMediaQuery to render the component conditionally
                    because the hooks will run only when React renders, causing layout shifts, afecting lighthouse scores.
                */}
                <Box display={["none", "none", "none", "flex"]} w="full">
                    {CreateImageMarker(image, markers, true)}
                </Box>
                <Box display={["flex", "flex", "flex", "none"]} h="100%" w="full">
                    {CreateImageMarker(mobileImage, mobileMarkers, false)}
                </Box>
                {(title || description) && (
                    <>
                        {gradient && (
                            <Flex
                                position="absolute"
                                h="100%"
                                top="0"
                                flexDirection="column"
                                justifyContent="end"
                                w="100%"
                                zIndex={0}
                                backgroundImage={[
                                    "linear-gradient(to bottom, rgba(0, 0, 0, .42) 25%, rgba(0, 0, 0, 0.02) 48.44%, rgba(0, 0, 0, 0) 100%)",
                                    "linear-gradient(to bottom, rgba(0, 0, 0, .42) 25%, rgba(0, 0, 0, 0.02) 48.44%, rgba(0, 0, 0, 0) 100%)",
                                    "linear-gradient(rgba(0, 0, 0, 0.42) 10%, rgba(0, 0, 0, 0.02) 30%, rgba(0, 0, 0, 0) 50%)",
                                ]}
                            ></Flex>
                        )}
                        <Flex
                            top={[4, 6]}
                            position={"absolute"}
                            direction={"column"}
                            w={["100%", "auto"]}
                            textAlign={"start"}
                            zIndex={1}
                            px={[4, 6]}
                            color={gradient ? "white" : "black"}
                        >
                            {title && (
                                <Text
                                    fontSize={[
                                        "mobileHeading1",
                                        "desktopHeading2",
                                        "desktopHeading2",
                                    ]}
                                    fontWeight="bold"
                                >
                                    {title}
                                </Text>
                            )}
                            {description && (
                                <Text
                                    fontSize={["mobileBodyTextNormal", "desktopBodyTextNormal"]}
                                    fontWeight="bold"
                                    mt={[2, 3]}
                                >
                                    {description}
                                </Text>
                            )}
                        </Flex>
                    </>
                )}
            </Flex>
        </Box>
    );
};
